import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import Cookies from 'js-cookie';
import authService from './services/authService';

// Configurable login redirect path (can override via env var)
const LOGIN_PATH = process.env.REACT_APP_LOGIN_PATH || '/login';

// Centralized forced logout (idempotent) to avoid duplicate cookie clearing logic
function forceLogout(reason = 'unauthorized') {
  if (window.__isForcingLogout) return; // prevent cascading calls
  window.__isForcingLogout = true;
  try {
    authService.logout();
  } catch (_) {
    // Fallback manual cookie purge
    ['token','refreshToken','userId','username','isAdmin'].forEach(k => Cookies.remove(k));
  }
  // Small async to allow console logs to flush
  setTimeout(() => {
    window.location.replace(`${LOGIN_PATH}?reason=${encodeURIComponent(reason)}`);
  }, 0);
}

// Simple universal 401 handling: override fetch so ANY 401 logs user out.
// Keeps it minimal; no need to refactor service files.
(() => {
  if (window.__fetch401Patched) return; // idempotent
  const originalFetch = window.fetch.bind(window);
  const TOKEN_ERROR_PATTERNS = [
    /token/i,
    /jwt/i,
    /expired/i,
    /invalid\s+(token|signature)/i,
    /unauthenticated/i
  ];

  async function isTokenIssue(resp) {
    try {
      // Header hint
      const www = resp.headers.get('www-authenticate');
      if (www && /bearer/i.test(www)) return true;
      // Peek body (clone so original remains readable by callers)
      const clone = resp.clone();
      const text = await clone.text();
      if (!text) return false;
      return TOKEN_ERROR_PATTERNS.some(r => r.test(text));
    } catch (_) {
      return false;
    }
  }

  window.fetch = async (input, init = {}) => {
    let resp;
    try {
      resp = await originalFetch(input, init);
    } catch (e) {
      // Network error: do NOT auto-logout unless you prefer prior aggressive behavior.
      console.warn('🌐 Global fetch network error (no logout):', e?.message || e);
      throw e; // allow caller to decide (UI can show offline state)
    }
    if (resp && resp.status === 401) {
      if (await isTokenIssue(resp)) {
        console.warn('🔐 Global 401 (token issue) -> logout');
        forceLogout('unauthorized');
        throw new Error('Unauthorized (auto-logout)');
      } else {
        console.warn('🔐 Global 401 (non-token) returned without forced logout');
        return resp; // caller can handle specific 401
      }
    }
    return resp;
  };
  window.__fetch401Patched = true;
})();

// Maintain globalFetch alias (optional backwards compatibility)
window.globalFetch = window.fetch;


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
